/**
 * AggregationDashboard component
 * Requirements: 16.1, 16.2, 16.7, 16.8, 16.9 - Full aggregation dashboard UI
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { subMonths, endOfDay, startOfDay } from 'date-fns';
import { aggregationApi } from '../api/aggregationApi';
import { DistributionChart } from './DistributionChart';
import { PeriodSelector } from './PeriodSelector';
import { ExportButtons } from './ExportButtons';
import type {
  AggregationResult,
  AggregationScope,
  DateRange,
  DepartmentOption,
  ExportFormat,
  PeriodPreset,
} from '../types';

type ChartType = 'histogram' | 'boxplot';

interface InsufficientDataError {
  error: string;
  message: string;
  minRequired: number;
  actual: number;
}

export const AggregationDashboard: React.FC = () => {
  // State
  const [scope, setScope] = useState<AggregationScope>('department');
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [aggregationResult, setAggregationResult] =
    useState<AggregationResult | null>(null);
  const [chartType, setChartType] = useState<ChartType>('histogram');
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('month');
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    startDate: startOfDay(subMonths(new Date(), 1)),
    endDate: endOfDay(new Date()),
  }));

  // Loading/Error states
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insufficientData, setInsufficientData] =
    useState<InsufficientDataError | null>(null);

  // Load departments on mount
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setIsLoadingDepartments(true);
        const depts = await aggregationApi.getDepartments();
        setDepartments(depts);
      } catch (err) {
        console.error('Failed to load departments:', err);
        setError('部門一覧の取得に失敗しました');
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    loadDepartments();
  }, []);

  // Fetch aggregation data
  const fetchAggregationData = useCallback(async () => {
    if (scope === 'department' && !selectedDepartmentId) {
      return;
    }

    try {
      setIsLoadingData(true);
      setError(null);
      setInsufficientData(null);

      const targetId =
        scope === 'department' ? selectedDepartmentId : 'current-company';

      const result = await aggregationApi.getAggregation(
        scope,
        targetId,
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
      setAggregationResult(result);
    } catch (err: unknown) {
      console.error('Failed to fetch aggregation data:', err);

      // Check for insufficient data error
      const axiosError = err as {
        response?: { status: number; data: InsufficientDataError };
      };
      if (
        axiosError.response?.status === 400 &&
        axiosError.response?.data?.error === 'INSUFFICIENT_DATA'
      ) {
        setInsufficientData(axiosError.response.data);
        setAggregationResult(null);
      } else {
        setError('集計データの取得に失敗しました');
      }
    } finally {
      setIsLoadingData(false);
    }
  }, [scope, selectedDepartmentId, dateRange]);

  // Fetch data when selection changes
  useEffect(() => {
    if (scope === 'company' || selectedDepartmentId) {
      fetchAggregationData();
    }
  }, [scope, selectedDepartmentId, dateRange, fetchAggregationData]);

  // Handlers
  const handleScopeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newScope: AggregationScope | null) => {
      if (newScope) {
        setScope(newScope);
        setAggregationResult(null);
        setError(null);
        setInsufficientData(null);
        if (newScope === 'company') {
          setSelectedDepartmentId('');
        }
      }
    },
    []
  );

  const handleDepartmentChange = useCallback(
    (event: { target: { value: string } }) => {
      setSelectedDepartmentId(event.target.value);
      setAggregationResult(null);
      setError(null);
      setInsufficientData(null);
    },
    []
  );

  const handleChartTypeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newType: ChartType | null) => {
      if (newType) {
        setChartType(newType);
      }
    },
    []
  );

  const handlePeriodChange = useCallback(
    (preset: PeriodPreset, newDateRange: DateRange) => {
      setPeriodPreset(preset);
      setDateRange(newDateRange);
    },
    []
  );

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      if (!aggregationResult) return;

      try {
        setIsExporting(true);
        const downloadUrl = await aggregationApi.exportAggregation(
          scope,
          aggregationResult.targetId,
          format,
          dateRange.startDate || undefined,
          dateRange.endDate || undefined
        );

        // Trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `aggregation-${aggregationResult.targetName}-${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Export failed:', err);
        setError('エクスポートに失敗しました');
      } finally {
        setIsExporting(false);
      }
    },
    [aggregationResult, scope, dateRange]
  );

  return (
    <Box data-testid="aggregation-dashboard" sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        思考傾向集計ダッシュボード
      </Typography>

      {/* Controls Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          {/* Scope Selector */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box data-testid="scope-selector">
              <Typography variant="subtitle2" gutterBottom>
                集計対象
              </Typography>
              <ToggleButtonGroup
                value={scope}
                exclusive
                onChange={handleScopeChange}
                aria-label="集計対象"
                size="small"
              >
                <ToggleButton value="department" aria-label="部門別">
                  部門別
                </ToggleButton>
                <ToggleButton value="company" aria-label="全社">
                  全社
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>

          {/* Department Selector */}
          {scope === 'department' && (
            <Grid size={{ xs: 12, md: 4 }} data-testid="department-selector">
              <FormControl fullWidth size="small" disabled={isLoadingDepartments}>
                <InputLabel id="department-select-label">部門を選択</InputLabel>
                <Select
                  labelId="department-select-label"
                  id="department-select"
                  value={selectedDepartmentId}
                  label="部門を選択"
                  onChange={handleDepartmentChange}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Chart Type Selector */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              グラフ形式
            </Typography>
            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={handleChartTypeChange}
              aria-label="グラフ形式"
              size="small"
            >
              <ToggleButton value="histogram" aria-label="ヒストグラム">
                ヒストグラム
              </ToggleButton>
              <ToggleButton value="boxplot" aria-label="箱ひげ図">
                箱ひげ図
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Period Selector */}
        <PeriodSelector
          selectedPreset={periodPreset}
          dateRange={dateRange}
          onChange={handlePeriodChange}
        />
      </Paper>

      {/* Export Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1">データエクスポート</Typography>
          <ExportButtons
            onExport={handleExport}
            isExporting={isExporting}
            disabled={!aggregationResult}
          />
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Insufficient Data Warning */}
      {insufficientData && (
        <Alert
          severity="warning"
          data-testid="insufficient-data-warning"
          sx={{ mb: 3 }}
        >
          {insufficientData.message}
          <br />
          現在のデータ数: {insufficientData.actual}人 / 必要最低人数:{' '}
          {insufficientData.minRequired}人
        </Alert>
      )}

      {/* Loading State */}
      {isLoadingData && (
        <Box
          data-testid="loading-indicator"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Results Section */}
      {aggregationResult && !isLoadingData && (
        <Paper sx={{ p: 3 }}>
          {/* Summary */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {aggregationResult.targetName}の思考傾向分布
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`対象: ${aggregationResult.employeeCount}人`}
                color="primary"
                variant="outlined"
              />
              {aggregationResult.period && (
                <Chip
                  label={`期間: ${new Date(aggregationResult.period.startDate).toLocaleDateString('ja-JP')} - ${new Date(aggregationResult.period.endDate).toLocaleDateString('ja-JP')}`}
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* Distribution Chart */}
          <DistributionChart
            distribution={aggregationResult.scoreDistribution}
            chartType={chartType}
            showStatistics
          />
        </Paper>
      )}

      {/* Empty State */}
      {!aggregationResult &&
        !isLoadingData &&
        !error &&
        !insufficientData &&
        scope === 'department' &&
        !selectedDepartmentId && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              部門を選択してください
            </Typography>
          </Paper>
        )}
    </Box>
  );
};
